import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { usePublicSite } from "@/contexts/PublicSiteContext";
import {
  analyticsPageFields, analyticsRuntimeAllowed, browserConsentStorage,
  consentStorageKey, normalizeHostname, normalizeMeasurementId,
  readConsentRecord, writeConsent,
} from "@/lib/analyticsConsentPolicy";
import { pauseAnalytics, recordJourneyClick, recordPageView, resumeAnalytics, revokeAnalytics } from "@/lib/analyticsRuntime";

export default function AnalyticsConsent() {
  const { settings, requestKey } = usePublicSite();
  const location = useLocation();
  const measurementId = normalizeMeasurementId(settings.ga4_measurement_id);
  const hostname = normalizeHostname(settings.ga4_hostname);
  const key = consentStorageKey(hostname, measurementId);
  // The context retains preview provenance after navigation removes the URL token.
  const preview = Boolean((requestKey || "").split("|")[1]);
  const allowed = !preview && settings.ga4_remote_settings_confirmed === true && analyticsRuntimeAllowed({
    enabled: settings.ga4_enabled, measurementId, configuredHostname: hostname,
    location: window.location, framed: window.self !== window.top,
  });
  const [record, setRecord] = useState(() => ({ key, value: readConsentRecord(browserConsentStorage(), hostname, measurementId) }));
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const choice = record.key === key && record.value?.expiresAt > Date.now() ? record.value.choice : "";

  useEffect(() => {
    const sync = () => {
      const value = readConsentRecord(browserConsentStorage(), hostname, measurementId);
      if (value?.choice !== "granted" && revokeAnalytics(measurementId) && value?.expiresAt !== Number.MAX_SAFE_INTEGER) {
        window.location.reload();
        return;
      }
      setRecord({ key, value });
    };
    const changed = event => { if (!event.key || event.key === key) sync(); };
    const visible = () => { if (document.visibilityState === "visible") sync(); };
    sync();
    const timer = window.setInterval(sync, 30000);
    window.addEventListener("storage", changed);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("storage", changed);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [key, hostname, measurementId]);

  useEffect(() => {
    if (!allowed || choice !== "granted") {
      pauseAnalytics(measurementId);
      return undefined;
    }
    const fields = analyticsPageFields(window.location.origin, location.pathname);
    if (!resumeAnalytics(measurementId, fields)) {
      setError("Un autre outil de mesure est présent. La collecte Orkelys reste désactivée.");
      return undefined;
    }
    recordPageView(measurementId, fields, `${location.key}:${location.pathname}`);
    const click = event => {
      if (event.button !== 0) return;
      if (readConsentRecord(browserConsentStorage(), hostname, measurementId)?.choice !== "granted") {
        pauseAnalytics(measurementId);
        return;
      }
      recordJourneyClick(measurementId, fields, event.target);
    };
    document.addEventListener("click", click);
    return () => {
      document.removeEventListener("click", click);
      pauseAnalytics(measurementId);
    };
  }, [allowed, choice, measurementId, hostname, location.key, location.pathname]);

  if (!allowed) return null;

  const decide = value => {
    const stored = writeConsent(browserConsentStorage(), hostname, measurementId, value);
    if (!stored && value === "granted") {
      setError("Votre choix ne peut pas être mémorisé. La mesure d’audience reste désactivée.");
      return;
    }
    setRecord({ key, value: readConsentRecord(browserConsentStorage(), hostname, measurementId) });
    setError(stored ? "" : "Refus appliqué pour cette page ; votre navigateur ne permet pas de le mémoriser.");
    setEditing(false);
    if (value === "denied" && revokeAnalytics(measurementId) && stored) window.location.reload();
  };

  return <>
    {(!choice || editing) && <section className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-3xl rounded-2xl border border-sage/25 bg-white p-5 shadow-2xl" role="region" aria-labelledby="analytics-consent-title" aria-describedby="analytics-consent-description">
      <h2 id="analytics-consent-title" className="font-playfair text-xl text-charcoal">Mesure d’audience</h2>
      <p id="analytics-consent-description" className="mt-2 font-manrope text-sm leading-relaxed text-charcoal-muted">Avec votre accord, Google Analytics mesure les visites et les accès aux pages de contact, de tarifs et de réservation. Les champs de formulaire, titres éditoriaux et sujets d’accompagnement ne sont pas transmis par ce module. Votre choix est conservé 180 jours et peut être retiré à tout moment.</p>
      <p className="mt-2 text-xs text-charcoal-muted">Le retrait arrête la collecte future et recharge la page. Il ne supprime pas les données déjà transmises à Google.</p>
      <div className="mt-4 flex flex-wrap justify-end gap-3">
        <button type="button" onClick={() => decide("denied")} className="btn-secondary min-h-11">Refuser</button>
        <button type="button" onClick={() => decide("granted")} className="btn-secondary min-h-11">Accepter</button>
      </div>
    </section>}
    {choice && !editing && <button type="button" onClick={() => setEditing(true)} className="fixed bottom-3 left-3 z-[90] min-h-11 rounded-full border border-sage/20 bg-white/95 px-3 py-2 font-manrope text-xs text-charcoal-muted shadow-sm hover:text-charcoal">Gérer les cookies</button>}
    {error && <p role="status" className="fixed inset-x-3 top-3 z-[101] mx-auto max-w-3xl rounded-xl border border-sage/25 bg-white p-3 text-sm text-charcoal">{error}</p>}
  </>;
}
